import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type DietType = 'paleo' | 'keto' | 'carnivore' | 'iifym' | 'balanced';

export interface Macros {
    protein: number;
    carbs: number;
    fats: number;
}

export interface DayLog {
    date: string;
    calories: number;
    macros: Macros;
    waterOz: number;
    weight?: number;
}

// AI Meal Plan Types
export interface Meal {
    name: string;
    calories: number;
    description: string;
    macros: Macros;
}

export interface DayPlan {
    day: string;
    meals: {
        breakfast: Meal;
        lunch: Meal;
        dinner: Meal;
        snack: Meal;
    };
    totalCalories: number;
    totalMacros: Macros;
}

interface NutritionState {
    dietType: DietType;
    restrictions: string[];
    weeklyPlan: DayPlan[] | null;
    isGenerating: boolean;

    // AI Usage Tracking
    aiUsage: {
        count: number;
        lastUsed: string; // ISO Date string YYYY-MM-DD
    };
    isPro: boolean;

    targetCalories: number;
    targetMacros: Macros;
    targetWaterOz: number;

    // Simple today log (in a real app, this would be a map or db table in Dexie)
    today: DayLog;

    setDietType: (diet: DietType) => void;
    setRestrictions: (restrictions: string[]) => void;
    setWeeklyPlan: (plan: DayPlan[]) => void;
    setGenerating: (g: boolean) => void;

    incrementAiUsage: () => void;
    resetAiUsage: () => void; // For testing/upgrading
    setPro: (isPro: boolean) => void;

    logWater: (amount: number) => void;
    updateMacros: (macros: Partial<Macros>) => void;
    updateCalories: (cals: number) => void;
}

const DEFAULT_TARGETS: Record<DietType, Macros> = {
    balanced: { protein: 180, carbs: 250, fats: 80 },
    paleo: { protein: 200, carbs: 150, fats: 100 },
    keto: { protein: 140, carbs: 30, fats: 160 },
    carnivore: { protein: 220, carbs: 0, fats: 180 },
    iifym: { protein: 180, carbs: 220, fats: 70 },
};

export const useNutritionStore = create<NutritionState>()(
    persist(
        (set) => ({
            dietType: 'balanced',
            restrictions: [],
            weeklyPlan: null,
            isGenerating: false,
            aiUsage: { count: 0, lastUsed: new Date().toISOString().split('T')[0] || '' },
            isPro: false,

            targetCalories: 2400,
            targetMacros: DEFAULT_TARGETS.balanced,
            targetWaterOz: 120, // Gallon-ish

            today: {
                date: new Date().toISOString().split('T')[0] || '',
                calories: 0,
                macros: { protein: 0, carbs: 0, fats: 0 },
                waterOz: 0
            },

            setDietType: (diet) => set({
                dietType: diet,
                targetMacros: DEFAULT_TARGETS[diet]
            }),

            setRestrictions: (restrictions) => set({ restrictions }),
            setWeeklyPlan: (weeklyPlan) => set({ weeklyPlan }),
            setGenerating: (isGenerating) => set({ isGenerating }),

            incrementAiUsage: () => set((state) => {
                const today = new Date().toISOString().split('T')[0] || '';
                // Reset count if new day
                if (state.aiUsage.lastUsed !== today) {
                    return { aiUsage: { count: 1, lastUsed: today } };
                }
                return { aiUsage: { ...state.aiUsage, count: state.aiUsage.count + 1 } };
            }),

            resetAiUsage: () => set((state) => ({
                aiUsage: { ...state.aiUsage, count: 0 }
            })),

            setPro: (isPro) => set({ isPro }),

            logWater: (amount) => set((state) => ({
                today: { ...state.today, waterOz: Math.min(state.today.waterOz + amount, 200) } // Cap visual
            })),

            updateMacros: (macros) => set((state) => ({
                today: {
                    ...state.today,
                    macros: { ...state.today.macros, ...macros },
                    calories: state.today.calories +
                        ((macros.protein || 0) * 4) +
                        ((macros.carbs || 0) * 4) +
                        ((macros.fats || 0) * 9)
                }
            })),

            updateCalories: (cals) => set((state) => ({
                today: { ...state.today, calories: state.today.calories + cals }
            }))
        }),
        {
            name: 'nutrition-storage',
        }
    )
);
